import { useState } from "react";
import { toast } from "react-toastify";
import { EQueueType } from "src/Types/ReDoc/EQueue";
import { DamaskRedirectParamType } from "src/Types/ReDoc/Damask";
import { toStringPayload, toRedirectPayload, hasActionError, getActionErrorMessage, returnToastStyle } from "../eQueueUtils";

const formatQueueCount = (n: number) => {
	if (n % 10 === 1 && n % 100 !== 11) return `${n} посетитель`;
	if ([2, 3, 4].includes(n % 10) && ![12, 13, 14].includes(n % 100)) return `${n} посетителя`;
	return `${n} посетителей`;
};

interface UseEQActionsParams {
	type: EQueueType;
	isReturnQueue: boolean;
	idQ: string;
	pultId: string;
	queueCount: number;
	showError: (message: string, title: string) => void;
	setShowWidget: (v: boolean) => void;
	dispatch: (action: string, payload?: unknown) => void;
	getQuery: (term?: string) => unknown;
	call: () => unknown;
	callById: (payload: string) => unknown;
	beginPause: (payload: { reason: string; idQ: string }) => unknown;
	endPause: () => unknown;
	drop: (payload: string) => unknown;
	recall: () => unknown;
	beginServing: () => unknown;
	delay: (payload: string) => unknown;
	redirect: (payload: DamaskRedirectParamType) => unknown;
	redirectWindow: (payload: string) => unknown;
	getDenyReasons: () => unknown;
	reason: (payload: string) => unknown;
	endServing: (payload: string) => unknown;
	exit: (payload: string) => unknown;
	start: (payload: string) => unknown;
	getWindows: () => unknown;
}

export const useEQActions = (params: UseEQActionsParams) => {
	const {
		type, isReturnQueue, idQ, pultId, queueCount, showError, setShowWidget,
		dispatch, getQuery, call, callById, beginPause, endPause, drop,
		recall, beginServing, delay, redirect, redirectWindow,
		getDenyReasons, reason, endServing, exit, start, getWindows
	} = params;

	const [ postCompleteAction, setPostCompleteAction ] = useState<string | null>(null);

	const onCloseModal = () => dispatch("closeModal");

	const openPostCompleteModal = (action: string) => {
		switch (action) {
			case "endServiceAndInviteByNumber":
				if (type === EQueueType.Enter) getQuery();
				dispatch("inviteByNumberModal");
				break;
			case "endServiceAndCap":
				dispatch("askCapModal");
				break;
			case "endServiceAndExit":
				dispatch("askExitModal");
				break;
		}
	};

	const runPostCompleteAction = () => {
		onCloseModal();
		if (postCompleteAction) {
			openPostCompleteModal(postCompleteAction);
			setPostCompleteAction(null);
		}
	};

	const handleEndServiceWithPostAction = (action: string) => {
		if (type === EQueueType.Enter) {
			getDenyReasons();
			setPostCompleteAction(action);
			dispatch("askEndServiceModal");
		} else {
			openPostCompleteModal(action);
		}
	};

	const openInviteByNumberModal = (shouldLoadQueue: boolean = false, searchTerm: string = "") => {
		if (shouldLoadQueue) {
			if (isReturnQueue) getQuery(searchTerm);
			if (type === EQueueType.Enter) getQuery();
		}
		dispatch("inviteByNumberModal");
	};

	const setReturnFreeState = () => {
		dispatch("setViewMode", "free");
		dispatch("clearMockTicket");
	};

	const onActionHandler = (actionName: string, payload?: unknown) => {
		if (window.isDev) console.log("Action - ", actionName, payload);

		switch (actionName) {
			case "asc-inviteByNumber":
				openInviteByNumberModal(type === EQueueType.Enter);
				break;
			case "cap":
				dispatch("askCapModal");
				break;
			case "cancelCap":
				dispatch("cancelCapModal");
				break;
			case "asc-notAppear":
				dispatch("askCancelCall");
				break;
			case "asc-delay":
				dispatch("ascDelay");
				break;
			case "asc-redirect":
				dispatch("ascRedirect");
				break;
			case "asc-exit":
				dispatch("askExitModal");
				break;
			case "invite":
				call();
				break;
			case "inviteByNumber": {
				if (isReturnQueue) {
					const ticketId = toStringPayload(payload);
					if (!ticketId) { showError("Не удалось определить талон для вызова", "Вызов по талону"); break; }
					callById(ticketId);
					break;
				}
				callById(toStringPayload(payload));
				break;
			}
			case "beginPause":
				beginPause({ reason: toStringPayload(payload), idQ });
				onCloseModal();
				break;
			case "endPause":
				endPause();
				break;
			case "notAppear":
				drop(idQ || "");
				break;
			case "repeatCall":
				recall();
				break;
			case "beginServing":
				beginServing();
				break;
			case "delay":
				delay(toStringPayload(payload));
				onCloseModal();
				break;
			case "redirect": {
				const redirectPayload = toRedirectPayload(payload);
				if (!!redirectPayload) redirect(redirectPayload);
				onCloseModal();
				break;
			}
			case "endService":
				if (type === EQueueType.Enter) {
					getDenyReasons();
					dispatch("askEndServiceModal");
				} else if (isReturnQueue) {
					setReturnFreeState();
				} else {
					endServing(idQ || "");
				}
				break;
			case "complete":
				endServing(idQ || "");
				runPostCompleteAction();
				break;
			case "reasons":
				reason(toStringPayload(payload));
				runPostCompleteAction();
				break;
			case "endServiceAndInviteByNumber":
			case "endServiceAndCap":
			case "endServiceAndExit":
				handleEndServiceWithPostAction(actionName);
				break;
			case "exit":
				exit(idQ);
				setShowWidget(false);
				break;
			case "return-open-window":
				if (!!pultId) start(pultId);
				dispatch("setViewMode", "free");
				break;
			case "return-invite":
				dispatch("setViewMode", "confirming");
				break;
			case "return-invite-queue":
				call();
				break;
			case "return-invite-ticket":
				openInviteByNumberModal(true);
				break;
			case "return-search-ticket":
				getQuery(toStringPayload(payload));
				break;
			case "return-confirm":
				dispatch("setViewMode", "called");
				break;
			case "return-invite-another":
				openInviteByNumberModal(true);
				break;
			case "return-repeat-call":
				dispatch("setViewMode", "called");
				break;
			case "return-stop-call":
				setReturnFreeState();
				break;
			case "return-stop-call-notappear":
				drop("");
				break;
			case "return-stop-call-rest":
				setReturnFreeState();
				beginPause({ reason: "", idQ });
				break;
			case "return-stop-call-finish":
				setReturnFreeState();
				dispatch("askExitModal");
				break;
			case "return-redirect":
				getWindows();
				break;
			case "return-redirect-select": {
				const toWindow = toStringPayload(payload);
				if (!toWindow) { showError("Не удалось определить окно для перенаправления", "Перенаправление"); break; }
				Promise.resolve(redirectWindow(toWindow)).then((res: unknown) => {
					if (hasActionError(res)) {
						showError(getActionErrorMessage(res) || "Не удалось перенаправить посетителя", "Перенаправление");
					}
				});
				break;
			}
			case "goRest":
				beginPause({ reason: "", idQ });
				break;
			case "finishWork":
				if ( queueCount > 0 ) {
					toast.error(
						`В очереди ещё ожидают ${formatQueueCount(queueCount)} — их талоны останутся активными`,
						{
							position: "top-center",
							autoClose: 10000,
							hideProgressBar: true,
							closeOnClick: true,
							pauseOnHover: true,
							draggable: true,
							closeButton: false,
							style: returnToastStyle
						}
					);
				}
				exit(idQ);
				break;
		}
	};

	return { onActionHandler, onCloseModal, postCompleteAction, setPostCompleteAction };
};
